import type { CombatEvent } from "@endclock/shared";
import type { Camera } from "../engine/camera.js";

export const COMBAT_AUDIO_VOICES = 8;
/** Owned procedural sounds, ordered so heavy shots can replace a quieter small gun at the cap. */
export const COMBAT_AUDIO_PROFILES = Object.freeze({
  hound_gun: { length: .085, range: 100, volume: .027, frequency: 1900, priority: 1 },
  gun: { length: .11, range: 125, volume: .04, frequency: 1350, priority: 2 },
  quad_burst: { length: 1.2, range: 165, volume: .070, frequency: 1050, priority: 3 },
  rocket_launch: { length: .35, range: 150, volume: .055, frequency: 1450, priority: 3 },
  cannon: { length: .85, range: 220, volume: .19, frequency: 700, priority: 5 },
  explosion: { length: 1.15, range: 230, volume: .22, frequency: 500, priority: 6 },
  disabled: { length: .65, range: 145, volume: .10, frequency: 650, priority: 4 },
});
type SoundKind = keyof typeof COMBAT_AUDIO_PROFILES;
interface Voice { source: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode; pan: StereoPannerNode; score: number }
/** Gesture-unlocked synthesis, distance falloff, stereo direction and a fixed voice cap. */
export class CombatAudio {
  private context: AudioContext | undefined;
  private readonly buffers = new Map<SoundKind, AudioBuffer>();
  private readonly voices = new Set<Voice>();
  private readonly eye = [0, 0, 0];
  private readonly right = [1, 0, 0];
  private played = 0;
  private replaced = 0;
  private disposed = false;
  private readonly unlock = (): void => {
    if (this.disposed || typeof AudioContext === "undefined") return;
    this.context ??= new AudioContext();
    void this.context.resume().catch(() => { /* Browser muting is harmless. */ });
    this.removeUnlock();
  };
  constructor() {
    if (typeof window === "undefined") return;
    window.addEventListener("pointerdown", this.unlock, { once: true });
    window.addEventListener("keydown", this.unlock, { once: true });
  }
  listener(camera: Camera): void {
    this.eye[0] = camera.position[0]; this.eye[1] = camera.position[1]; this.eye[2] = camera.position[2];
    this.right[0] = camera.view[0]!; this.right[1] = camera.view[4]!; this.right[2] = camera.view[8]!;
  }
  emit(event: CombatEvent): void {
    const context = this.context, kind = event.kind;
    if (!context || context.state !== "running" || !(kind in COMBAT_AUDIO_PROFILES)) return;
    const sound = kind as SoundKind, profile = COMBAT_AUDIO_PROFILES[sound];
    const point = sound === "explosion" || sound === "disabled" ? event.to : event.from;
    const dx = point[0] - this.eye[0]!, dy = point[1] - this.eye[1]!, dz = point[2] - this.eye[2]!;
    const distance = Math.hypot(dx, dy, dz), range = profile.range;
    if (distance >= range || !Number.isFinite(distance)) return;
    const score = profile.priority * (1 - distance / range);
    if (this.voices.size >= COMBAT_AUDIO_VOICES) {
      let weakest: Voice | undefined;
      for (const voice of this.voices) if (!weakest || voice.score < weakest.score) weakest = voice;
      if (!weakest || weakest.score >= score) return;
      this.endVoice(weakest, true); this.replaced++;
    }
    const length = sound === "quad_burst" ? Math.min(1.2, Math.max(.08, (event.durationMs ?? 750) / 1000)) : profile.length;
    const gain = context.createGain(), filter = context.createBiquadFilter(), pan = context.createStereoPanner(), source = context.createBufferSource();
    let buffer = this.buffers.get(sound);
    if (!buffer) { buffer = this.synthesize(context, sound); this.buffers.set(sound, buffer); }
    source.buffer = buffer;
    if (sound === "quad_burst") source.playbackRate.value = .075 * Math.min(16, Math.max(1, event.rounds ?? 12)) / length;
    const volume = profile.volume * (1 - distance / range) ** 2 / (1 + distance * .018);
    filter.type = sound === "hound_gun" || sound === "gun" || sound === "quad_burst" ? "highpass" : "lowpass";
    filter.frequency.setValueAtTime(profile.frequency, context.currentTime);
    if (sound === "cannon" || sound === "explosion") filter.frequency.exponentialRampToValueAtTime(160, context.currentTime + length);
    pan.pan.value = Math.max(-.9, Math.min(.9, (dx * this.right[0]! + dy * this.right[1]! + dz * this.right[2]!) / Math.max(1, distance)));
    gain.gain.setValueAtTime(.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(Math.max(.0002, volume), context.currentTime + .004);
    // A quad's rhythm is in its one cached buffer; no audio node or network event per round.
    if (sound === "quad_burst") gain.gain.setValueAtTime(Math.max(.0002, volume), context.currentTime + Math.max(.006, length - .035));
    gain.gain.exponentialRampToValueAtTime(.0001, context.currentTime + length);
    source.connect(filter); filter.connect(gain); gain.connect(pan); pan.connect(context.destination);
    const voice: Voice = { source, gain, filter, pan, score }; this.voices.add(voice); this.played++;
    source.onended = () => this.endVoice(voice, false);
    source.start(); source.stop(context.currentTime + length + .02);
  }
  reset(): void { for (const voice of this.voices) this.endVoice(voice, true); }
  snapshot() { return { unlocked: this.context?.state === "running", voices: this.voices.size, maximumVoices: COMBAT_AUDIO_VOICES,
    played: this.played, replaced: this.replaced, cachedSounds: this.buffers.size }; }
  dispose(): void { this.disposed = true; this.removeUnlock(); this.reset(); this.buffers.clear(); void this.context?.close(); }
  private endVoice(voice: Voice, stop: boolean): void {
    if (!this.voices.delete(voice)) return;
    voice.source.onended = null;
    if (stop) try { voice.source.stop(); } catch { /* Already ended. */ }
    voice.source.disconnect(); voice.filter.disconnect(); voice.gain.disconnect(); voice.pan.disconnect();
  }
  private removeUnlock(): void {
    if (typeof window === "undefined") return;
    window.removeEventListener("pointerdown", this.unlock); window.removeEventListener("keydown", this.unlock);
  }
  private synthesize(context: AudioContext, kind: SoundKind): AudioBuffer {
    const profile = COMBAT_AUDIO_PROFILES[kind];
    const buffer = context.createBuffer(1, Math.ceil(profile.length * context.sampleRate), context.sampleRate), data = buffer.getChannelData(0);
    let seed = 70917, rumble = 0;
    for (let index = 0; index < data.length; index++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const noise = seed / 2147483648 - 1, t = index / context.sampleRate;
      rumble += (noise - rumble) * .07;
      if (kind === "cannon" || kind === "explosion" || kind === "disabled") {
        const frequency = kind === "cannon" ? 65 : kind === "disabled" ? 95 : 42;
        data[index] = noise * Math.exp(-t * 48) * .66 + (rumble * 1.2 + Math.sin(t * Math.PI * 2 * frequency)) * Math.exp(-t * 4.5) * .32;
      } else if (kind === "quad_burst") {
        const phase = t % .075;
        data[index] = noise * Math.exp(-phase * 100) * .8 + Math.sin(phase * 1800) * Math.exp(-phase * 150) * .14;
      } else if (kind === "rocket_launch") data[index] = (noise * .7 + rumble * .3) * Math.exp(-t * 5);
      else data[index] = noise * Math.exp(-t * 45) * .9;
    }
    return buffer;
  }
}
